/** @jsxImportSource @emotion/react */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { jsx, css } from '@emotion/react'
import { get } from 'lodash'
import { withRouter } from 'react-router-dom'
import AdminMainLayout from './AdminMainLayout'
import { Container, Col, Row, Modal } from 'react-bootstrap'
import BusyMask from '../../components/BusyMask'
import Loading from '../../components/Loading'
import Card from '../../components/layout/Card'
import CardInfoText from '../../components/layout/CardInfoText'
import CommonTable from '../../components/CommonTable'
import { Separator } from '../../components/layout/Separator'
import { BlueButton } from '../../components/layout/BlueButton'
import { WhiteButton } from '../../components/layout/WhiteButton'
import { Error } from '../../components/layout/Error'
import { showSuccess, showError } from '../../actions/Error'
import { handleSubmitResult } from '../../actions/form'
import { globalSettingsList } from '../../actions/settings'
import { adminEditableEmailList } from '../actions/admin_editable_email'
import { adminSavedSearchesCustomerList } from '../actions/admin_saved_searches_customer'
import { adminSavedSearchesSubscriptionList } from '../actions/admin_saved_searches_subscription'
import { adminBulkEmailList, adminBulkEmailStartList } from '../actions/admin_bulk_email'

/* Send a manual email to everyone matching one saved search.

   The recipients are worked out when the run starts, not here - this page only picks the email
   and the search. */

const saved_search_headers = {
    name: { name: 'Name' },
    action: { name: '' },
}

class AdminEditableEmailRun extends Component {

    constructor(props) {
        super(props)
        this.state = {selected_type: null,
                      selected_item: null,
                      show_customer_modal: false,
                      show_subscription_modal: false,
                      // The run that has been prepared but not sent yet - shows the confirmation.
                      pending_run: null}
    }

    componentDidMount() {
        const { dispatch, editable_email_id } = this.props
        // Refresh deliberately: whether this email can be sent is decided from the loaded copy, and
        // ensureObjectLoaded will not refetch an object the list page already cached.
        dispatch(adminEditableEmailList.invalidateObject(editable_email_id))
        dispatch(adminEditableEmailList.ensureObjectLoaded(editable_email_id))
        dispatch(globalSettingsList.ensureGlobalSettingsLoaded())

        // Cleared first so both tables list every saved search, whatever filter another page left on them.
        dispatch(adminSavedSearchesCustomerList.clearListFilter())
        dispatch(adminSavedSearchesCustomerList.fetchListIfNeeded())
        dispatch(adminSavedSearchesSubscriptionList.clearListFilter())
        dispatch(adminSavedSearchesSubscriptionList.fetchListIfNeeded())
    }

    componentDidUpdate(prev_props) {
        const { dispatch, editable_email_id } = this.props
        dispatch(adminEditableEmailList.ensureObjectLoaded(editable_email_id))
    }

    onShowSavedSearchModal = (type) => {
        this.setState(type === 'customer' ? {show_customer_modal: true} : {show_subscription_modal: true})
    }

    onHideSavedSearchModal = (type) => {
        this.setState(type === 'customer' ? {show_customer_modal: false} : {show_subscription_modal: false})
    }

    onSelect = (type, item) => {
        // Picking closes the modal - the name then shows next to the button that opened it.
        this.setState({selected_type: type,
                       selected_item: item,
                       show_customer_modal: false,
                       show_subscription_modal: false})
    }

    isSelected = (type, item) => {
        const { selected_type, selected_item } = this.state
        return selected_type === type && !!selected_item && selected_item.id === item.id
    }

    onPrepare = () => {
        const { dispatch, editable_email_id } = this.props
        const { selected_type, selected_item } = this.state
        const that = this

        // Resolves the recipients and records the run, but nothing is sent yet - the response
        // says how many that came to, which is what the confirmation shows.
        const on_ok = function(json) {
            that.setState({pending_run: json})
        }

        return dispatch(adminBulkEmailList.prepareBulkEmail({
            editable_email: editable_email_id,
            saved_search_customer: selected_type === 'customer' ? selected_item.id : null,
            saved_search_subscription: selected_type === 'subscription' ? selected_item.id : null,
        })).then((res) => handleSubmitResult({res, on_ok}))
    }

    onConfirm = () => {
        const { dispatch, history } = this.props
        const { pending_run } = this.state
        const that = this

        const on_ok = function() {
            showSuccess("Sending", "The email is being sent to each recipient")
            history.push('/admin/manual_editable_emails')
        }

        return dispatch(adminBulkEmailStartList.startBulkEmail(pending_run.id))
                 .then((res) => handleSubmitResult({res, on_ok}))
    }

    onCancelPending = () => {
        this.setState({pending_run: null})
    }

    getSavedSearchCellValue = (type) => {
        const that = this
        return function(header_key, item, index) {
            switch( header_key ) {
                case 'name':
                    // The whole cell is the target, so pick a search from anywhere in the row.
                    return (
                        <div onClick={ () => that.onSelect(type, item) } css={ cell_style }>
                          <a css={ name_style }>{item.name}</a>
                        </div>
                    )
                case 'action':
                    return (
                        <input type="radio"
                               name="saved_search"
                               checked={ that.isSelected(type, item) }
                               onChange={ () => that.onSelect(type, item) } />
                    )
                default:
                    return undefined
            }
        }
    }

    renderConfirmModal = () => {
        const that = this
        const { pending_run, selected_item, selected_type } = this.state
        const { editable_email, is_busy } = this.props

        // What the recipients are called depends on which kind of saved search was picked.
        const audience = selected_type === 'customer' ? 'customers' : 'subscriptions'
        const email_name = get(editable_email, ["description"], '')

        return (
            <Modal show={true}
                   onHide={ that.onCancelPending } >
              <Modal.Header closeButton>
                <Modal.Title>
                  Confirm send
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                { is_busy && <Loading /> }
                <div css={ summary_style }>
                  Email will be sent to <b>{ pending_run.num_pending }</b> { audience } for
                  email <b>{ email_name }</b>.
                </div>
                <Separator variant="h20" />
                <CardInfoText>
                  Saved search: { selected_item.name }
                </CardInfoText>
              </Modal.Body>

              <Modal.Footer>
                <WhiteButton onClick={ that.onCancelPending } auto_disable={false}>
                  Cancel
                </WhiteButton>
                <BlueButton onClick={ that.onConfirm } auto_disable={false}>
                  Send
                </BlueButton>
              </Modal.Footer>
            </Modal>
        )
    }

    renderSavedSearchModal = (type) => {
        const that = this
        const is_customer = type === 'customer'
        const { customer_searches, customer_searches_loading,
                subscription_searches, subscription_searches_loading } = this.props

        return (
            <Modal show={true}
                   onHide={ () => that.onHideSavedSearchModal(type) }
                   size="lg" >
              <Modal.Header closeButton>
                <Modal.Title>
                  { is_customer ? 'Customer' : 'Subscription' } saved searches
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <CardInfoText>
                  Click a saved search to send to everyone it currently matches.
                </CardInfoText>
                <CommonTable is_loading={ is_customer ? customer_searches_loading : subscription_searches_loading }
                             empty_message={ is_customer ? "There are no saved customer searches."
                                                         : "There are no saved subscription searches." }
                             headers={ saved_search_headers }
                             items={ is_customer ? customer_searches : subscription_searches }
                             item_list={ is_customer ? adminSavedSearchesCustomerList
                                                     : adminSavedSearchesSubscriptionList }
                             getCellValue={ that.getSavedSearchCellValue(type) }
                />
              </Modal.Body>
            </Modal>
        )
    }

    renderSavedSearchButton(type) {
        const { selected_type, selected_item } = this.state
        const is_customer = type === 'customer'
        const is_selected = selected_type === type && !!selected_item

        return (
            <div css={ button_row_style }>
              <BlueButton onClick={ () => this.onShowSavedSearchModal(type) } auto_disable={false}>
                Select { is_customer ? 'customer' : 'subscription' } search
              </BlueButton>
              { is_selected &&
                <span css={ selected_name_style }>{selected_item.name}</span>
              }
            </div>
        )
    }

    render() {
        const { editable_email, is_loading, is_busy, can_edit } = this.props
        const { selected_item, selected_type, pending_run,
                show_customer_modal, show_subscription_modal } = this.state
        const that = this

        // No language in the title - the same email is sent in every language, so the page should
        // read the same whichever language's row was used to get here.
        const title = get(editable_email, ["description"])

        // Fail closed: if the email has not loaded yet we cannot know it is ready, so do not
        // let it be sent. Treating a missing list as "nothing outstanding" would enable Send.
        const not_ready_languages = get(editable_email, ["not_ready_language_codes"], null)
        const readiness_known = not_ready_languages !== null
        const is_ready = readiness_known && not_ready_languages.length === 0

        return (
            <AdminMainLayout breadcrumbs={[{name: 'admin_home'},
                                           {name: 'manual_editable_emails',
                                            label: 'Manual Emails',
                                            url: '/admin/manual_editable_emails'},
                                           {name: 'editable_email_run',
                                            label: 'Send',
                                            url: `/admin/editable_email/${get(editable_email, ["id"], '')}/run`}
                                          ]}>
              <Container fluid>
                <Row className="justify-content-center">
                  <Col md="11" lg="9" xl="8">
                    { is_busy && <BusyMask /> }

                    {/* Only once we know - otherwise this flashes while the email is still loading. */}
                    { readiness_known && ! is_ready &&
                      <div css={ warning_style }>
                        Can't send until the email is configured in all languages
                      </div>
                    }

                    <h1>Send {title}</h1>
                    <Separator variant="h30" />

                    { is_loading && <Loading /> }

                    <Card title="Who to send it to">
                      <CardInfoText>
                        Pick one saved search. The recipients are worked out when you press send, and
                        each one gets this email in their own language.
                      </CardInfoText>

                      <Separator variant="h20" />
                      { that.renderSavedSearchButton('customer') }
                      <Separator variant="h10" />
                      { that.renderSavedSearchButton('subscription') }

                      <Separator variant="h30" />

                      {/* Either the selection and its Send, or the confirmation of what that Send
                          worked out - never both, so there is only ever one Send on the page. */}
                      { can_edit && ! pending_run &&
                        <div>
                          <div css={ selection_style }>
                            { selected_item
                              ? <span>Sending to: <b>{selected_item.name}</b>
                                  ({ selected_type === 'customer' ? 'customer search' : 'subscription search' })
                                </span>
                              : <span css={ nothing_selected_style }>Pick a saved search above.</span>
                            }
                          </div>
                          <Separator variant="h20" />
                          <BlueButton onClick={ that.onPrepare }
                                      auto_disable={false}
                                      disabled={ ! selected_item || ! is_ready }>
                            Send
                          </BlueButton>
                        </div>
                      }

                      {/* The confirmation is a modal, so the page behind it stays as the admin
                          left it if they back out. */}
                      { can_edit && pending_run && that.renderConfirmModal() }
                      { ! can_edit &&
                        <Error>Sending custom emails is disabled</Error>
                      }
                    </Card>

                    { show_customer_modal && that.renderSavedSearchModal('customer') }
                    { show_subscription_modal && that.renderSavedSearchModal('subscription') }
                  </Col>
                </Row>
              </Container>
            </AdminMainLayout>
        )
    }

}

function mapStateToProps(state, props) {
    const editable_email_id = get(props, ["match", "params", "editable_email_id"], null)
    return {
        editable_email_id,
        editable_email: adminEditableEmailList.getObject(editable_email_id),
        is_loading: adminEditableEmailList.isLoading(),
        is_busy: adminBulkEmailList.getIsSavingObject() || adminBulkEmailStartList.getIsSavingObject(),
        customer_searches: adminSavedSearchesCustomerList.getVisibleObjects() || [],
        customer_searches_loading: adminSavedSearchesCustomerList.isLoading(),
        subscription_searches: adminSavedSearchesSubscriptionList.getVisibleObjects() || [],
        subscription_searches_loading: adminSavedSearchesSubscriptionList.isLoading(),
        can_edit: globalSettingsList.getSetting('can_edit_editable_emails'),
    }
}

export default withRouter(connect(mapStateToProps)(AdminEditableEmailRun))

const warning_style = css`
background-color: #fff4e5;
border: 1px solid #ffb74d;
border-radius: 4px;
padding: 12px 16px;
margin-bottom: 20px;
`

const button_row_style = css`
display: flex;
align-items: center;
`

const selected_name_style = css`
margin-left: 12px;
font-weight: bold;
`

const selection_style = css`
font-size: 16px;
`

const summary_style = css`
font-size: 18px;
`


const nothing_selected_style = css`
color: #8e8e93;
`

const cell_style = css`
cursor: pointer;
display: block;
width: 100%;
min-height: 24px;
`

const name_style = css`
text-decoration: underline;
`
